import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSocket } from '../contexts/SocketContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { LogOut, Plus, Trash2, Search, Moon, Sun, CheckCircle, QrCode, History, Camera, Upload, Tag, Volume2, VolumeX } from 'lucide-react';


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getApiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((entry) => entry?.msg || entry?.message)
      .filter(Boolean);
    if (messages.length) return messages.join(', ');
  }
  if (detail && typeof detail === 'object') {
    return detail.message || detail.description || JSON.stringify(detail);
  }
  return error?.message || fallback;
}

export default function CashierDashboard() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { socket } = useSocket();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [orders, setOrders] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountPaid, setAmountPaid] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [pendingUpiPayment, setPendingUpiPayment] = useState(null);
  const [cartLookup, setCartLookup] = useState('');
  const [foundCart, setFoundCart] = useState(null);
  const [cartPaymentMethod, setCartPaymentMethod] = useState('cash');
  const [cartAmountPaid, setCartAmountPaid] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [historyOrders, setHistoryOrders] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [confirmingCartPayment, setConfirmingCartPayment] = useState(false);
  const [paymentSoundMuted, setPaymentSoundMuted] = useState(() => localStorage.getItem('cashierPaymentSoundMuted') === 'true');
  const [updatingOrderIds, setUpdatingOrderIds] = useState({});
  const [sidebarTab, setSidebarTab] = useState('current-order');
  const [availableOffers, setAvailableOffers] = useState([]);
  const [selectedOfferId, setSelectedOfferId] = useState('none');
  const [offerOpen, setOfferOpen] = useState(false);
  const [savingOffer, setSavingOffer] = useState(false);
  const [offerForm, setOfferForm] = useState({
    name: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    start_date: '',
    end_date: '',
    min_cart_value: '',
    promo_code: '',
  });
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);

  useEffect(() => {
    if (user?.role !== 'cashier' && user?.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchCategories();
    fetchMenuItems();
    fetchOrders();
    fetchHistory();
    fetchPaymentHistory();
    fetchAvailableOffers();
  }, [user, navigate]);

  useEffect(() => {
    if (!socket) return;

    const handleNewOrder = () => {
      fetchOrders();
      toast.info('New order received!');
    };

    const handleOrderUpdate = (updatedOrder) => {
      setOrders((prev) =>
        prev.map((o) => (o.id === updatedOrder.id ? updatedOrder : o)).filter(
          (o) => o.status !== 'completed' && o.status !== 'cancelled'
        )
      );
    };

    const handleQueueRecalc = () => {
      fetchOrders();
      if (foundCart?.id) {
        searchCart(foundCart.cart_number);
      }
    };

    const handlePaymentSuccess = (payment) => {
      playPaymentReceivedSound(payment?.amount);
      toast.success(`Payment of ₹${Math.round(Number(payment?.amount || 0))} received`);
      setPendingUpiPayment(null);
      setFoundCart(null);
      setCartLookup('');
      setCartPaymentMethod('cash');
      setCartAmountPaid('');
      setCart([]);
      setCustomerName('');
      setSelectedOfferId('none');
      fetchOrders();
      fetchHistory();
      fetchPaymentHistory();
    };

    const handlePaymentCreated = (payment) => {
      setPendingUpiPayment(payment);
      fetchPaymentHistory();
    };

    socket.on('connect', fetchOrders);
    socket.on('new_order', handleNewOrder);
    socket.on('order_updated', handleOrderUpdate);
    socket.on('queue_recalculated', handleQueueRecalc);
    socket.on('payment_success', handlePaymentSuccess);
    socket.on('payment_created', handlePaymentCreated);

    return () => {
      socket.off('connect', fetchOrders);
      socket.off('new_order', handleNewOrder);
      socket.off('order_updated', handleOrderUpdate);
      socket.off('queue_recalculated', handleQueueRecalc);
      socket.off('payment_success', handlePaymentSuccess);
      socket.off('payment_created', handlePaymentCreated);
    };
  }, [socket, foundCart, paymentSoundMuted]);

  useEffect(() => {
    if (!user) return;
    const intervalId = setInterval(() => {
      fetchOrders();
    }, 2000);
    return () => clearInterval(intervalId);
  }, [user]);

  useEffect(() => {
    let items = menuItems;

    if (selectedCategory !== 'all') {
      items = items.filter((item) => item.category_id === selectedCategory);
    }

    if (searchQuery) {
      items = items.filter(
        (item) =>
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredItems(items);
  }, [menuItems, selectedCategory, searchQuery]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API}/menu-items`);
      setMenuItems(response.data.filter((item) => item.available));
    } catch (error) {
      console.error('Failed to fetch menu items:', error);
    }
  };

  async function fetchOrders() {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data.filter((o) => o.status !== 'completed' && o.status !== 'cancelled'));
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  }

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/orders/history`, { params: historySearch ? { search: historySearch } : {} });
      setHistoryOrders(response.data.slice(0, 10));
    } catch (error) {
      console.error('Failed to fetch order history', error);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const response = await axios.get(`${API}/payments/counter-history`);
      setPaymentHistory(Array.isArray(response.data) ? response.data.slice(0, 12) : []);
    } catch (error) {
      console.error('Failed to fetch payment history', error);
    }
  };

  const togglePaymentSound = () => {
    setPaymentSoundMuted((current) => {
      const next = !current;
      localStorage.setItem('cashierPaymentSoundMuted', String(next));
      return next;
    });
  };

  const playPaymentReceivedSound = (amount) => {
    if (paymentSoundMuted) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.18, audioContext.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.38);
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.start();
      osc.stop(audioContext.currentTime + 0.4);
    } catch (error) {
      console.warn('Audio notification unavailable', error);
    }
    try {
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(`Payment of rupees ${Math.round(Number(amount || 0))} has been received.`);
        utterance.rate = 0.95;
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utterance);
      }
    } catch (error) {
      console.warn('Voice notification unavailable', error);
    }
  };

  const fetchAvailableOffers = async () => {
    try {
      const response = await axios.get(`${API}/offers`);
      setAvailableOffers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch offers', error);
    }
  };

  const addToCart = (item) => {
    const existing = cart.find((c) => c.item_id === item.id);
    if (existing) {
      setCart(cart.map((c) => (c.item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c)));
    } else {
      setCart([...cart, { item_id: item.id, title: item.title, price: item.price, quantity: 1 }]);
    }
  };

  const removeFromCart = (item_id) => {
    setCart(cart.filter((c) => c.item_id !== item_id));
  };

  const updateQuantity = (item_id, quantity) => {
    if (quantity <= 0) {
      removeFromCart(item_id);
    } else {
      setCart(cart.map((c) => (c.item_id === item_id ? { ...c, quantity } : c)));
    }
  };

  const roundRupee = (value) => Math.round(Number(value || 0));

  const placeOrder = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    const total = roundRupee(payableTotal);

    if (paymentMethod === 'upi') {
      setPlacingOrder(true);
      try {
        const response = await axios.post(`${API}/cashier/payments/create`, {
          items: cart,
          total,
          customer_name: customerName.trim() || null,
          promo_code: selectedOffer?.promo_code || null,
        });
        setPendingUpiPayment(response.data);
        toast.success('UPI QR is ready on /pay. The order will be placed automatically after payment.');
      } catch (error) {
        console.error('Failed to create UPI payment:', error);
        toast.error(getApiErrorMessage(error, 'Failed to create UPI payment'));
      } finally {
        setPlacingOrder(false);
      }
      return;
    }

    const paid = parseFloat(amountPaid);
    if (!paid || paid < total) {
      toast.error('Amount paid is insufficient');
      return;
    }

    const previousCart = cart;
    const previousAmountPaid = amountPaid;
    const previousPaymentMethod = paymentMethod;

    const optimisticOrder = {
      id: `temp-${Date.now()}`,
      order_number: '... ',
      items: previousCart,
      total,
      status: 'new',
      created_at: new Date().toISOString(),
      optimistic: true,
    };

    setPlacingOrder(true);
    setOrders((prev) => [optimisticOrder, ...prev]);
    setCart([]);
    setAmountPaid('');
    setPaymentMethod('cash');
    setSelectedOfferId('none');

    try {
      await axios.post(`${API}/orders`, {
        items: previousCart,
        total,
        promo_code: selectedOffer?.promo_code || null,
        payment_method: previousPaymentMethod,
        amount_paid: parseFloat(previousAmountPaid),
      });
      setOrders((prev) => prev.filter((o) => o.id !== optimisticOrder.id));
      toast.success('Order placed successfully');
      fetchOrders();
    } catch (error) {
      setOrders((prev) => prev.filter((o) => o.id !== optimisticOrder.id));
      setCart(previousCart);
      setAmountPaid(previousAmountPaid);
      setPaymentMethod(previousPaymentMethod);
      toast.error('Failed to place order');
    } finally {
      setPlacingOrder(false);
    }
  };

  const markOrderDelivered = async (orderId) => {
    const previousOrders = orders;
    setUpdatingOrderIds((prev) => ({ ...prev, [orderId]: true }));
    setOrders((prev) => prev.filter((o) => o.id !== orderId));
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: 'completed' });
      toast.success('Order marked as delivered');
    } catch (error) {
      setOrders(previousOrders);
      toast.error('Failed to update order');
    } finally {
      setUpdatingOrderIds((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const createOffer = async (event) => {
    event.preventDefault();
    if (!offerForm.start_date || !offerForm.end_date) {
      toast.error('Start and end date are required');
      return;
    }

    const payload = {
      name: offerForm.name,
      description: offerForm.description || null,
      promo_code: offerForm.promo_code.trim() ? offerForm.promo_code.trim().toUpperCase() : null,
      start_date: new Date(offerForm.start_date).toISOString(),
      end_date: new Date(offerForm.end_date).toISOString(),
      discount_type: offerForm.discount_type,
      discount_percentage: offerForm.discount_type === 'percentage' ? Number(offerForm.discount_value) : null,
      flat_discount: offerForm.discount_type === 'flat' ? Number(offerForm.discount_value) : null,
      max_discount: null,
      min_cart_value: offerForm.min_cart_value ? Number(offerForm.min_cart_value) : null,
      free_item_product_id: null,
      buy_x_get_y: null,
      product_targeting: { apply_to: 'all_products', product_id: null, category_id: null },
      user_targeting: { target_type: 'all_users', emails: [] },
      usage_rules: { total_usage_limit: null, per_user_limit: null },
      allow_stacking: true,
      image_url: null,
      image_data: null,
    };

    setSavingOffer(true);
    try {
      await axios.post(`${API}/promotions`, payload);
      toast.success('Offer added successfully');
      setOfferOpen(false);
      setOfferForm({
        name: '',
        description: '',
        discount_type: 'percentage',
        discount_value: '',
        start_date: '',
        end_date: '',
        min_cart_value: '',
        promo_code: '',
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to add offer');
    } finally {
      setSavingOffer(false);
    }
  };

  const normalizeCartLookup = (rawValue) => {
    const raw = (rawValue ?? '').toString().trim();
    if (!raw) return '';
    if (raw.startsWith('{')) return raw;

    const upper = raw.toUpperCase();
    if (upper.startsWith('KC-')) return upper;
    const digits = upper.replace(/\D/g, '').slice(0, 4);
    return digits ? `KC-${digits}` : '';
  };

  const searchCart = async (valueOverride) => {
    const normalized = normalizeCartLookup(valueOverride ?? cartLookup);
    if (!normalized) return;
    try {
      const response = await axios.get(`${API}/cashier/carts/search`, { params: { cart_number: normalized } });
      setFoundCart(response.data);
    } catch (error) {
      toast.error('Cart not found');
      setFoundCart(null);
    }
  };

  const confirmCartPayment = async () => {
    if (!foundCart) return;
    const roundedTotal = roundRupee(foundCart.total || 0);
    const paid = cartAmountPaid ? parseFloat(cartAmountPaid) : roundedTotal;
    if (cartPaymentMethod === 'cash' && paid < roundedTotal) {
      toast.error('Amount paid is insufficient');
      return;
    }
    setConfirmingCartPayment(true);
    try {
      const response = await axios.post(`${API}/cashier/carts/confirm-payment`, {
        cart_number: foundCart.cart_number,
        payment_method: cartPaymentMethod,
        amount_paid: cartPaymentMethod === 'cash' ? paid : null,
      });
      if (cartPaymentMethod === 'upi') {
        setPendingUpiPayment(response.data);
        toast.success('UPI QR is ready on /pay. The cart will be confirmed automatically after payment.');
      } else {
        toast.success('Cart payment confirmed and added to queue');
        setFoundCart(null);
        setCartLookup('');
        setCartPaymentMethod('cash');
        setCartAmountPaid('');
        fetchOrders();
        fetchHistory();
      }
      fetchPaymentHistory();
    } catch (error) {
      console.error('Failed to confirm payment:', error);
      toast.error(getApiErrorMessage(error, 'Failed to confirm payment'));
    } finally {
      setConfirmingCartPayment(false);
    }
  };

  const startScanner = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error('Camera not supported on this browser. Use Upload QR.');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } } });
      streamRef.current = stream;
      setScannerOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 50);
    } catch (error) {
      toast.error('Unable to access camera');
    }
  };

  const stopScanner = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (scanIntervalRef.current) { clearInterval(scanIntervalRef.current); scanIntervalRef.current = null; }
    setScannerOpen(false);
  };

  const scanFromCamera = async (silent = false) => {
    if (!videoRef.current) return;
    if (!('BarcodeDetector' in window)) {
      toast.error('Barcode scanning is not supported on this device/browser');
      return;
    }

    try {
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const codes = await detector.detect(videoRef.current);
      if (!codes.length) {
        if (!silent) toast.error('No QR detected. Try again.');
        return false;
      }
      const value = codes[0].rawValue;
      const normalized = normalizeCartLookup(value);
      setCartLookup(normalized.startsWith('KC-') ? normalized.slice(3) : '');
      await searchCart(value);
      stopScanner();
      toast.success('QR scanned successfully');
      return true;
    } catch (error) {
      if (!silent) toast.error('QR scan failed');
      return false;
    }
  };


  const scanFromImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch('https://api.qrserver.com/v1/read-qr-code/', { method: 'POST', body: form });
      const data = await res.json();
      const value = data?.[0]?.symbol?.[0]?.data;
      if (!value) {
        toast.error('Unable to decode QR from image');
        return;
      }
      const normalized = normalizeCartLookup(value);
      setCartLookup(normalized.startsWith('KC-') ? normalized.slice(3) : '');
      await searchCart(value);
      toast.success('QR image decoded');
    } catch (e) {
      toast.error('QR image scan failed');
    } finally {
      event.target.value = '';
    }
  };


  useEffect(() => {
    if (!scannerOpen) return;
    scanIntervalRef.current = setInterval(() => {
      scanFromCamera(true);
    }, 700);
    return () => {
      if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    };
  }, [scannerOpen]);

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const eligibleOffers = availableOffers.filter((offer) => {
    const minCartValue = Number(offer?.min_cart_value || 0);
    const discountType = (offer?.discount_type || '').toLowerCase();
    return cartTotal >= minCartValue && ['percentage', 'flat'].includes(discountType);
  });
  const selectedOffer = eligibleOffers.find((offer) => offer.id === selectedOfferId);
  const offerDiscount = selectedOffer
    ? Math.min(
      cartTotal,
      (selectedOffer.discount_type || '').toLowerCase() === 'percentage'
        ? (cartTotal * Number(selectedOffer.discount_percentage || 0)) / 100
        : Number(selectedOffer.flat_discount || 0)
    )
    : 0;
  const payableTotal = Math.max(cartTotal - offerDiscount, 0);
  const payableRounded = roundRupee(payableTotal);
  const changeAmount = paymentMethod === 'cash' && amountPaid ? parseFloat(amountPaid) - payableRounded : 0;
  const readyOrders = orders.filter((o) => o.status === 'ready');
  const activeOrders = orders.filter((o) => o.status !== 'ready');

  useEffect(() => {
    if (selectedOfferId !== 'none' && !eligibleOffers.some((offer) => offer.id === selectedOfferId)) {
      setSelectedOfferId('none');
    }
  }, [selectedOfferId, eligibleOffers]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-3 py-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <div>
            <h1 className="font-sans text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50" data-testid="cashier-dashboard-title">
              Cashier Dashboard
            </h1>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Welcome, {user?.name}</p>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
                  <Tag className="w-4 h-4 mr-1" />Add Offer
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>Add Offer</DialogTitle>
                </DialogHeader>
                <form onSubmit={createOffer} className="space-y-3">
                  <div className="space-y-1">
                    <Label>Offer Name</Label>
                    <Input required value={offerForm.name} onChange={(e) => setOfferForm((prev) => ({ ...prev, name: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Description</Label>
                    <Textarea value={offerForm.description} onChange={(e) => setOfferForm((prev) => ({ ...prev, description: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label>Discount Type</Label>
                      <Select value={offerForm.discount_type} onValueChange={(value) => setOfferForm((prev) => ({ ...prev, discount_type: value, discount_value: '' }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">Percentage</SelectItem>
                          <SelectItem value="flat">Flat Amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>{offerForm.discount_type === 'percentage' ? 'Discount %' : 'Discount ₹'}</Label>
                      <Input required type="number" min="0" step="0.01" value={offerForm.discount_value} onChange={(e) => setOfferForm((prev) => ({ ...prev, discount_value: e.target.value }))} />
                    </div>
                    <div className="space-y-1">
                      <Label>Start Date</Label>
                      <Input required type="datetime-local" value={offerForm.start_date} onChange={(e) => setOfferForm((prev) => ({ ...prev, start_date: e.target.value }))} />
                    </div>
                    <div className="space-y-1">
                      <Label>End Date</Label>
                      <Input required type="datetime-local" value={offerForm.end_date} onChange={(e) => setOfferForm((prev) => ({ ...prev, end_date: e.target.value }))} />
                    </div>
                    <div className="space-y-1">
                      <Label>Min Cart Value (optional)</Label>
                      <Input type="number" min="0" step="0.01" value={offerForm.min_cart_value} onChange={(e) => setOfferForm((prev) => ({ ...prev, min_cart_value: e.target.value }))} />
                    </div>
                    <div className="space-y-1">
                      <Label>Promo Code (optional)</Label>
                      <Input value={offerForm.promo_code} onChange={(e) => setOfferForm((prev) => ({ ...prev, promo_code: e.target.value }))} />
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={savingOffer}>{savingOffer ? 'Saving...' : 'Save Offer'}</Button>
                </form>
              </DialogContent>
            </Dialog>
            <Button
              onClick={togglePaymentSound}
              variant="outline"
              size="sm"
              title={paymentSoundMuted ? 'Unmute payment success sound' : 'Mute payment success sound'}
              className="flex-1 sm:flex-none"
            >
              {paymentSoundMuted ? <VolumeX className="w-4 h-4 mr-1" /> : <Volume2 className="w-4 h-4 mr-1" />}
              {paymentSoundMuted ? 'Unmute' : 'Mute'}
            </Button>
            <Button onClick={toggleTheme} variant="outline" size="icon" data-testid="cashier-theme-toggle" className="flex-none">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              variant="outline"
              size="sm"
              data-testid="cashier-logout-button"
            >
              <LogOut className="w-4 h-4 mr-1" />Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-3 space-y-3">
        {pendingUpiPayment && pendingUpiPayment.status === 'PENDING' && (
          <div className="rounded-3xl border border-cyan-200 bg-gradient-to-r from-cyan-50 to-emerald-50 p-4 shadow-sm dark:border-cyan-900/60 dark:from-cyan-950/30 dark:to-emerald-950/20">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-cyan-700 dark:text-cyan-300">UPI payment awaiting confirmation</p>
                <p className="text-xs text-slate-600 dark:text-slate-300">Ask the customer to scan the QR on <strong>/pay</strong>. The order will be placed automatically after payment.</p>
              </div>
              <Badge className="w-fit bg-cyan-600">₹{Math.round(Number(pendingUpiPayment.amount || 0))}</Badge>
            </div>
          </div>
        )}
        <Card className="rounded-3xl border-slate-200/80 shadow-sm dark:border-slate-800">
          <CardContent className="p-3 space-y-2">
            <p className="font-semibold text-sm">Cart Number / QR Checkout</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex items-center border rounded-md px-3 min-h-10 w-full bg-background">
                <span className="text-sm font-mono text-slate-500">KC-</span>
                <Input
                  value={cartLookup}
                  onChange={(e) => setCartLookup(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="1234"
                  className="border-0 shadow-none focus-visible:ring-0 px-2"
                  inputMode="numeric"
                />
              </div>
              <Button onClick={() => searchCart()} className="w-full sm:w-auto"><Search className="w-4 h-4 mr-1" />Find</Button>
              <Button variant="outline" onClick={startScanner} className="w-full sm:w-auto"><Camera className="w-4 h-4 mr-1" />Camera</Button>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full sm:w-auto"><Upload className="w-4 h-4 mr-1" />Upload QR</Button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={scanFromImageUpload} />
            </div>
            {scannerOpen && (
              <div className="border rounded p-2 space-y-2">
                <video ref={videoRef} className="w-full max-h-64 rounded bg-black" muted playsInline />
                <div className="flex gap-2">
                  <Button onClick={scanFromCamera}><QrCode className="w-4 h-4 mr-1" />Scan QR</Button>
                  <Button variant="outline" onClick={stopScanner}>Close Camera</Button>
                </div>
              </div>
            )}
            {foundCart && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 space-y-3 dark:border-emerald-900/60 dark:bg-emerald-950/20">
                <div className="flex justify-between items-center"><span className="font-mono font-bold">#{foundCart.cart_number}</span><Badge>{foundCart.cart_status}</Badge></div>
                <p className="text-sm">{(foundCart.items || []).length} items • payable ₹{roundRupee(foundCart.total || 0)} <span className="text-xs text-slate-500">(rounded from ₹{Number(foundCart.total || 0).toFixed(2)})</span></p>
                <div className="max-h-28 overflow-y-auto text-xs space-y-1 rounded-xl border bg-white/80 p-2 dark:bg-slate-900/60">
                  {(foundCart.items || []).map((it, idx) => <p key={idx}>{it.quantity}x {it.title}</p>)}
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label>Payment Method</Label>
                    <Select value={cartPaymentMethod} onValueChange={setCartPaymentMethod}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {cartPaymentMethod === 'cash' && (
                    <div className="space-y-1">
                      <Label>Amount Paid (₹)</Label>
                      <Input type="number" step="1" value={cartAmountPaid} onChange={(e) => setCartAmountPaid(e.target.value)} placeholder={`${roundRupee(foundCart.total || 0)}`} />
                    </div>
                  )}
                </div>
                <Button onClick={confirmCartPayment} disabled={confirmingCartPayment || Boolean(pendingUpiPayment)} className="w-full">{confirmingCartPayment ? 'PROCESSING' : cartPaymentMethod === 'upi' ? 'Create UPI QR on /pay' : 'Confirm Cash Payment'}</Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-3">
          <div className="space-y-3">
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input placeholder="Search menu items..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-menu-input" />
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                <Button onClick={() => setSelectedCategory('all')} variant={selectedCategory === 'all' ? 'default' : 'outline'} size="sm">All Items</Button>
                {categories.map((category) => (
                  <Button key={category.id} onClick={() => setSelectedCategory(category.id)} variant={selectedCategory === category.id ? 'default' : 'outline'} size="sm">
                    {category.name}
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {filteredItems.map((item) => (
                <Card key={item.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => addToCart(item)}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1">
                        <h3 className="font-semibold">{item.title}</h3>
                        <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-1">{item.description}</p>
                        <p className="text-lg font-bold mt-1">₹{item.price}</p>
                      </div>
                      <Button size="sm" onClick={(e) => { e.stopPropagation(); addToCart(item); }}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {readyOrders.length > 0 && (
              <div>
                <h2 className="text-lg font-bold mb-2">Ready Orders</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {readyOrders.map((order) => (
                    <Card key={order.id} className="border-green-500 border-2">
                      <CardContent className="p-4 space-y-2">
                        <div className="flex justify-between items-start">
                          <p className="font-mono text-lg font-bold">#{order.order_number}</p>
                          <Badge className="bg-green-500">Ready</Badge>
                        </div>
                        <div className="space-y-1">
                          {order.items.map((item, idx) => <p key={idx} className="text-sm">{item.quantity}x {item.title}</p>)}
                        </div>
                        <div className="flex justify-between items-center">
                          <p className="font-bold">₹{order.total.toFixed(2)}</p>
                          <Button size="sm" disabled={Boolean(updatingOrderIds[order.id])} onClick={() => markOrderDelivered(order.id)} className="bg-green-600 hover:bg-green-700">
                            <CheckCircle className="w-4 h-4 mr-1" />{updatingOrderIds[order.id] ? 'Updating...' : 'Delivered'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 rounded-lg flex flex-col h-[75vh] lg:h-[84vh]">
            <Tabs value={sidebarTab} onValueChange={setSidebarTab} className="p-4 border-b border-slate-200 dark:border-slate-800">
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="current-order">Current Order</TabsTrigger>
                <TabsTrigger value="active-orders">Active Orders</TabsTrigger>
                <TabsTrigger value="order-history">Order History</TabsTrigger>
              </TabsList>
            </Tabs>

            {sidebarTab === 'current-order' && (
              <>
                <div className="h-80 lg:h-96 overflow-y-auto p-4 border-b border-slate-200 dark:border-slate-800">
                  <h2 className="text-xl font-bold mb-3">Current Order</h2>
                  {cart.length === 0 ? (
                    <p className="text-center text-slate-500 dark:text-slate-400 py-8">Cart is empty</p>
                  ) : (
                    <div className="space-y-2">
                      {cart.map((item) => (
                        <div key={item.item_id} className="flex items-center gap-2 p-2 border rounded">
                          <div className="flex-1">
                            <p className="font-semibold text-sm">{item.title}</p>
                            <p className="text-xs text-slate-600 dark:text-slate-400">₹{item.price} each</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateQuantity(item.item_id, item.quantity - 1)}>-</Button>
                            <span className="w-8 text-center text-sm">{item.quantity}</span>
                            <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateQuantity(item.item_id, item.quantity + 1)}>+</Button>
                            <Button size="sm" variant="destructive" className="h-7 w-7 p-0" onClick={() => removeFromCart(item.item_id)}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {cart.length > 0 && (
                  <div className="p-4 space-y-3">
                    <div className="space-y-2">
                      <Label>Payment Method</Label>
                      <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="upi">UPI</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {paymentMethod === 'cash' && (
                      <div className="space-y-2 rounded-2xl border bg-slate-50 p-3 dark:bg-slate-900/60">
                        <Label>Amount Paid (₹)</Label>
                        <Input type="number" step="1" value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} placeholder={`Enter at least ₹${payableRounded}`} />
                        {amountPaid && changeAmount >= 0 && <p className="text-sm">Change: <span className="font-bold">₹{changeAmount.toFixed(2)}</span></p>}
                      </div>
                    )}

                    {paymentMethod === 'upi' && (
                      <div className="space-y-2 rounded-2xl border border-cyan-200 bg-cyan-50 p-3 dark:border-cyan-900/60 dark:bg-cyan-950/20">
                        <Label>Customer Name (optional)</Label>
                        <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Walk-in Customer" />
                        <p className="text-xs text-slate-600 dark:text-slate-300">A live UPI QR will appear on /pay. Confirmation is automatic through Razorpay webhook.</p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Eligible Offer</Label>
                      <Select value={selectedOfferId} onValueChange={setSelectedOfferId}>
                        <SelectTrigger><SelectValue placeholder="Select an offer" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No offer</SelectItem>
                          {eligibleOffers.map((offer) => (
                            <SelectItem key={offer.id} value={offer.id}>
                              {offer.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedOffer && (
                        <p className="text-xs text-slate-600 dark:text-slate-400">
                          Applied: {selectedOffer.name} ({selectedOffer.discount_type === 'percentage' ? `${Number(selectedOffer.discount_percentage || 0)}%` : `₹${Number(selectedOffer.flat_discount || 0).toFixed(2)}`} off)
                        </p>
                      )}
                      {eligibleOffers.length === 0 && (
                        <p className="text-xs text-slate-500 dark:text-slate-400">No offers available for this cart value.</p>
                      )}
                    </div>

                    <Separator />
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span>Subtotal:</span><span>₹{cartTotal.toFixed(2)}</span></div>
                      <div className="flex justify-between text-green-600"><span>Offer discount:</span><span>-₹{offerDiscount.toFixed(2)}</span></div>
                    </div>
                    <div className="space-y-1"><div className="flex justify-between items-center text-lg font-bold"><span>Payable:</span><span>₹{payableRounded}</span></div><p className="text-right text-xs text-slate-500">Rounded from ₹{payableTotal.toFixed(2)}</p></div>
                    <Button onClick={placeOrder} disabled={placingOrder || Boolean(pendingUpiPayment)} className="w-full rounded-xl" size="lg">{placingOrder ? 'PROCESSING' : paymentMethod === 'upi' ? 'Create UPI QR' : 'Place Order'}</Button>
                  </div>
                )}
              </>
            )}

            {sidebarTab === 'active-orders' && (
              <div className="p-4 flex-1 min-h-[180px] overflow-y-auto">
                <h3 className="font-bold mb-2">Active Orders</h3>
                <div className="space-y-2 text-xs">
                  {activeOrders.map((order) => (
                    <div key={order.id} className="p-2 border rounded flex justify-between items-center">
                      <span className="font-mono font-bold">#{order.order_number}</span>
                      <span>Q#{order.queue_position || '-'}</span>
                      <Badge className={order.status === 'new' ? 'bg-amber-500' : order.status === 'cooking' ? 'bg-blue-500' : 'bg-slate-500'}>{order.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sidebarTab === 'order-history' && (
              <div className="p-4 flex-1 min-h-[180px] overflow-y-auto">
                <h3 className="font-bold mb-2 flex items-center"><History className="w-4 h-4 mr-1" />Order History</h3>
                <div className="flex gap-2 mb-2">
                  <Input value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} placeholder="Search cart/order/date" />
                  <Button variant="outline" onClick={fetchHistory}>Search</Button>
                </div>
                <div className="space-y-1 text-xs">
                  {historyOrders.map((order) => (
                    <div key={order.id} className="border rounded-xl p-2 flex justify-between">
                      <span>#{order.order_number} ({order.order_type})</span>
                      <span>{new Date(order.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
                <Separator className="my-3" />
                <div className="flex items-center justify-between">
                  <h4 className="font-bold">Transaction History</h4>
                  <Button size="sm" variant="outline" onClick={fetchPaymentHistory}>Refresh</Button>
                </div>
                <div className="mt-2 space-y-2 text-xs">
                  {paymentHistory.length === 0 && <p className="text-slate-500">No UPI counter transactions yet.</p>}
                  {paymentHistory.map((payment) => (
                    <div key={payment.id} className="rounded-xl border p-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-medium">{payment.customerName || 'Walk-in Customer'}</span>
                        <Badge className={payment.status === 'PAID' ? 'bg-emerald-600' : payment.status === 'PENDING' ? 'bg-amber-500 text-black' : 'bg-slate-500'}>{payment.status}</Badge>
                      </div>
                      <div className="mt-1 flex justify-between text-slate-500 dark:text-slate-400">
                        <span>₹{Math.round(Number(payment.amount || 0))}</span>
                        <span>{payment.order_number ? `Order #${payment.order_number}` : payment.cart_number || payment.source_type}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
